import { Component, computed, inject, input } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { SCOPED_ACCESS_GATE } from '@mb/auth/client';
import { PERMISSION_ACTION } from '@mb/auth/shared';
import { PERSON_SCOPED_ENTITY } from '@modules/contacts/shared';
import type { PositionAssigneeDto } from '../../../shared';
import { OrgChartStore } from './org-chart.state';

/**
 * Presentational person row shown under a position row. Wrapped by `cdkDrag`
 * in the position row so it can be dragged to another position.
 *
 * Deliberately flat — no border/shadow — so the list reads as lines under the
 * position rather than cards nested in a card. A primary assignment is
 * marked with a dot instead of a second text line, keeping rows single-line at
 * exactly `PERSON_ROW_HEIGHT` so the node's height maths stays exact.
 *
 * The pencil opens the person's details in the chart's sheet, for whoever may
 * read persons — the chart alone is readable with less. The row itself stays
 * a line to drag, not a button.
 */
@Component({
  selector: 'org-person-row',
  standalone: true,
  imports: [TranslocoPipe],
  template: `
    <span class="min-w-0 flex-1 truncate text-[13px] leading-4 text-slate-700">
      {{ name() }}
    </span>
    @if (person().isPrimary) {
      <span
        class="size-1.5 shrink-0 rounded-full bg-primary"
        [title]="'organigram.row.primary' | transloco"
        [attr.aria-label]="'organigram.row.primary' | transloco"
      ></span>
    }
    @if (canPreview()) {
      <button
        type="button"
        class="flex shrink-0 cursor-default items-center text-slate-400 opacity-0 transition-opacity hover:text-slate-700 group-hover/row:opacity-100 focus-visible:opacity-100"
        [title]="'organigram.row.openPerson' | transloco"
        data-no-drag="true"
        data-no-pan="true"
        (click)="onPreview($event)"
      >
        <span class="material-icons text-sm" aria-hidden="true">edit</span>
      </button>
    }
    @if (canManage()) {
      <button
        type="button"
        class="flex shrink-0 cursor-default items-center rounded-md text-slate-400 opacity-0 transition-opacity group-hover/row:opacity-100 hover:bg-destructive/10 hover:text-destructive focus-visible:opacity-100"
        [title]="'organigram.row.removeAssignment' | transloco"
        data-no-drag="true"
        data-no-pan="true"
        (click)="onDelete($event)"
      >
        <!--
          Material Icons has one weight, and its X is thin beside the filled
          pencil. A stroke in the text colour thickens it to match.
        -->
        <span class="material-icons text-sm [-webkit-text-stroke:1.5px_currentColor]" aria-hidden="true">close</span>
      </button>
    }
    <span
      class="material-icons shrink-0 text-sm text-slate-300 opacity-0 transition-opacity group-hover/row:opacity-100"
      aria-hidden="true"
      >drag_indicator</span
    >
  `,
  host: {
    class:
      'group/row flex h-8 items-center gap-2 rounded-md px-2 transition-colors hover:bg-slate-50',
    'data-no-drag': 'true',
    'data-no-pan': 'true',
  },
})
export class PersonRowComponent {
  readonly #store = inject(OrgChartStore);
  readonly #access = inject(SCOPED_ACCESS_GATE);
  readonly person = input.required<PositionAssigneeDto>();
  readonly canManage = input.required<boolean>();
  readonly name = computed(() =>
    `${this.person().firstName} ${this.person().lastName}`.trim(),
  );
  readonly canPreview = computed(() =>
    this.#access.can(PERSON_SCOPED_ENTITY, PERMISSION_ACTION.READ),
  );

  onPreview(event: Event): void {
    if (!this.canPreview()) return;
    event.stopPropagation();
    this.#store.previewPerson(this.person().personId);
  }

  onDelete(event: Event): void {
    if (!this.canManage()) return;
    event.stopPropagation();
    this.#store.remove({
      kind: 'personPosition',
      id: this.person().personPositionId,
    });
  }
}
